import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Lightbulb, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { formatDateTime } from '@/lib/dates'
import { useLearning, useLearnings, useSetLearningActive } from '@/queries/learnings'
import { EMPTY_LEARNING, type Learning } from '@/types/learning'
import { LearningEditor } from './LearningEditor'

const FILTER = 'h-[var(--etn-control-height-md)] rounded-etn-pill border border-etn-border-input bg-etn-surface px-3 text-etn-control'
const KIND_LABEL = { known_fix: 'Known fix', client_context: 'Client context' } as const

function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'warning' | 'success' }) {
  const tint = {
    neutral: 'bg-etn-control-subtle text-etn-text-label',
    warning: 'bg-etn-warning-subtle text-etn-warning-text',
    success: 'bg-etn-success-subtle text-etn-success-text'
  }[tone]
  return <span className={`inline-flex whitespace-nowrap rounded-etn-pill px-2.5 py-0.5 text-etn-micro font-semibold ${tint}`}>{children}</span>
}

function LearningDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: learning } = useLearning(id)
  const setActive = useSetLearningActive(id)
  if (!learning) return null
  return (
    <section className="space-y-4 rounded-etn-card border border-etn-border-hairline bg-etn-surface p-4" data-testid="learning-detail">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{learning.title}</h3>
        <Pill>Version {learning.version}</Pill>
        {!learning.active && <Pill>Retired</Pill>}
        <div className="ml-auto flex gap-2">
          <Button size="xs" variant="outline" disabled={setActive.isPending} onClick={() => setActive.mutate(!learning.active)}>
            {learning.active ? 'Retire' : 'Reactivate'}
          </Button>
          <Button size="xs" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
      <LearningEditor key={`${learning._id}-${learning.version}`} initial={learning} learning={learning} />
      {(learning.history?.length ?? 0) > 0 && (
        <div data-testid="learning-history">
          <h4 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Earlier versions</h4>
          <ol className="space-y-2">
            {[...(learning.history ?? [])].reverse().map(v => (
              <li key={v.version} className="rounded-etn-card bg-etn-surface-sunken p-3 text-xs">
                <p className="font-semibold">
                  Version {v.version}, replaced {formatDateTime(v.editedAt)}
                </p>
                <p className="text-etn-text-cell">{v.snapshot.title}</p>
                <p className="whitespace-pre-wrap text-etn-text-soft">{v.snapshot.kind === 'known_fix' ? v.snapshot.symptoms : v.snapshot.body}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  )
}

function outcomes(l: Learning): string {
  const { appliedCount = 0, dismissedCount = 0 } = l.stats ?? {}
  return `${appliedCount} applied, ${dismissedCount} dismissed`
}

/**
 * Admin, Learnings: everything staff have taught the Support Agent, how often each known fix has
 * answered a ticket, and how staff have received its proposals.
 */
export function LearningsPage({ selectedId }: { selectedId?: string }) {
  const navigate = useNavigate()
  const [filters, setFilters] = useState({ kind: '', scope: '', status: 'active', needsReview: '' })
  const [creating, setCreating] = useState(false)
  const { data, isLoading } = useLearnings(filters)
  const rows = data?.data ?? []
  const select = (id?: string) => navigate({ to: '/admin/learnings', search: id ? { id } : {} })

  return (
    <div className="space-y-5" data-testid="learnings-page">
      <div className="flex flex-wrap items-end gap-3 rounded-etn-card bg-etn-surface-sunken p-3">
        <label className="space-y-1">
          <span className="block text-etn-micro font-bold text-etn-text-label">Kind</span>
          <select aria-label="Kind" className={FILTER} value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))}>
            <option value="">All</option>
            <option value="known_fix">Known fix</option>
            <option value="client_context">Client context</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="block text-etn-micro font-bold text-etn-text-label">Scope</span>
          <select aria-label="Scope" className={FILTER} value={filters.scope} onChange={e => setFilters(f => ({ ...f, scope: e.target.value }))}>
            <option value="">All</option>
            <option value="global">Every company</option>
            <option value="company">One company</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="block text-etn-micro font-bold text-etn-text-label">Status</span>
          <select aria-label="Status" className={FILTER} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </select>
        </label>
        <label className="flex h-[var(--etn-control-height-md)] items-center gap-2 text-etn-control">
          <input
            type="checkbox"
            checked={filters.needsReview === 'true'}
            onChange={e => setFilters(f => ({ ...f, needsReview: e.target.checked ? 'true' : '' }))}
          />
          Needs review only
        </label>
        <Button className="ml-auto" size="sm" onClick={() => setCreating(true)}>
          <Plus />
          New learning
        </Button>
      </div>

      {creating && (
        <section className="rounded-etn-card border border-etn-border-hairline bg-etn-surface p-4">
          <h3 className="mb-3 text-sm font-semibold">New learning</h3>
          <LearningEditor
            initial={EMPTY_LEARNING}
            onCancel={() => setCreating(false)}
            onSaved={saved => {
              setCreating(false)
              select(saved._id)
            }}
          />
        </section>
      )}

      {selectedId && <LearningDetail id={selectedId} onClose={() => select()} />}

      {!isLoading && rows.length === 0 ? (
        <EmptyState icon={<Lightbulb />} title="No learnings yet" body="Save one from a completed investigation, or create one here." />
      ) : (
        <div className="overflow-x-auto rounded-etn-card border border-etn-border-hairline">
          <table className="w-full text-left text-etn-control" data-testid="learnings-table">
            <thead className="text-etn-micro uppercase tracking-[0.05em] text-etn-text-label">
              <tr>
                {['Title', 'Kind', 'Scope', 'Matches', 'Last matched', 'Outcomes', 'Status'].map(h => (
                  <th key={h} className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l._id} className="h-11 border-t border-etn-border-row hover:bg-etn-surface-sunken" data-testid={`learning-row-${l._id}`}>
                  <td className="px-3 font-semibold">
                    <Link to="/admin/learnings" search={{ id: l._id }} className="hover:underline">
                      {l.title}
                    </Link>
                    {l.needsReview && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-etn-micro font-semibold text-etn-warning-text"
                        data-testid="needs-review"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Needs review
                      </span>
                    )}
                  </td>
                  <td className="px-3">{KIND_LABEL[l.kind]}</td>
                  <td className="px-3">{l.scope === 'global' ? 'Every company' : 'One company'}</td>
                  <td className="px-3 tabular-nums">{l.kind === 'known_fix' ? (l.stats?.matchedCount ?? 0) : ''}</td>
                  <td className="px-3">{l.stats?.lastMatchedAt ? formatDateTime(l.stats.lastMatchedAt) : ''}</td>
                  <td className="px-3">{l.kind === 'known_fix' ? outcomes(l) : ''}</td>
                  <td className="px-3">{l.active ? <Pill tone="success">Active</Pill> : <Pill>Retired</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
